import { LitElement, html, css, nothing } from 'lit';
import { StoreMixin } from '../store.js';
import { api, ApiError } from '../api.js';
import { OVERRIDE_META, describeOverride, overrideMeta } from '../lib/scenarios.js';

const PLANNING_CATEGORIES = ['early', 'mid', 'late'];

/**
 * One scenario's assumptions (PRD 7.5; TRD 10.3; Phase 35): the two toggles that belong to the
 * scenario itself, and the list of overrides, each with the form that adds another.
 *
 * The editor is built around one rule: **a scenario says what it changes, and nothing else.**
 * Every row here is a difference from the base forecast, written in the same words the list and
 * the banner use (`lib/scenarios.js`), and a scenario with no rows says plainly that it computes
 * to the base. That is what makes the recompute readable: a planner who can see the whole list
 * of changes on one screen can tell whether a number moved for the reason they asked for.
 *
 * Every write goes straight to the API and the scenario comes back from the server — none of
 * this state is patched locally. An override is a row with an author and a date; a screen that
 * guessed at what the server stored could show a scenario that does not exist.
 */
class ScenarioEditor extends StoreMixin(LitElement) {
  static properties = {
    scenario: { type: Object },
    _adding: { state: true },
    _draft: { state: true },
    _saving: { state: true },
    _error: { state: true },
    _options: { state: true },
  };

  constructor() {
    super();
    this.scenario = null;
    this._adding = null;
    this._draft = {};
    this._saving = false;
    this._error = null;
    this._options = { projects: [], deals: [], stages: [], sources: [], loaded: false };
  }

  static styles = css`
    :host {
      display: block;
    }
    .grid {
      display: grid;
      grid-template-columns: minmax(0, 1fr);
      gap: 16px;
    }
    h3 {
      margin: 0 0 6px;
      font-size: 13px;
      letter-spacing: 0.02em;
      color: var(--ink-strong);
    }
    p.hint {
      margin: 0 0 10px;
      font-size: 12.5px;
      color: var(--ink-muted);
      max-width: 70ch;
    }
    .toggles {
      display: flex;
      flex-wrap: wrap;
      gap: 18px;
      align-items: center;
      font-size: 13px;
    }
    label.inline {
      display: inline-flex;
      align-items: center;
      gap: 6px;
    }
    select,
    input[type='text'],
    input[type='number'],
    input[type='date'] {
      font: inherit;
      font-size: 13px;
      padding: 5px 8px;
      border: 1px solid var(--border);
      border-radius: 6px;
      background: var(--surface);
      color: var(--ink);
      max-width: 100%;
    }
    table {
      width: 100%;
      border-collapse: collapse;
      font-size: 13px;
    }
    th {
      text-align: left;
      font-size: 11px;
      letter-spacing: 0.08em;
      text-transform: uppercase;
      color: var(--ink-muted);
      border-bottom: 1px solid var(--border-soft);
      padding: 0 8px 6px 0;
      font-weight: 600;
    }
    td {
      padding: 7px 8px 7px 0;
      border-bottom: 1px solid var(--border-soft);
      vertical-align: top;
    }
    td.subject {
      color: var(--ink-strong);
    }
    .empty {
      font-size: 13px;
      color: var(--ink-muted);
      padding: 8px 0;
    }
    button {
      font: inherit;
      font-size: 12.5px;
      padding: 5px 10px;
      border-radius: 6px;
      border: 1px solid var(--border);
      background: var(--surface);
      color: var(--ink);
      cursor: pointer;
    }
    button.primary {
      background: var(--link);
      border-color: var(--link);
      color: var(--surface);
    }
    button.link {
      border: 0;
      background: none;
      color: var(--link);
      padding: 0;
    }
    button:disabled {
      opacity: 0.5;
      cursor: default;
    }
    .add {
      display: flex;
      flex-wrap: wrap;
      gap: 8px;
      align-items: center;
      margin-top: 10px;
    }
    .kinds {
      display: flex;
      flex-wrap: wrap;
      gap: 6px;
    }
    .form {
      display: flex;
      flex-wrap: wrap;
      gap: 8px;
      align-items: center;
      margin-top: 10px;
      padding: 10px;
      border: 1px dashed var(--border);
      border-radius: 8px;
    }
    .form .sentence {
      flex-basis: 100%;
      font-size: 12.5px;
      color: var(--ink-muted);
    }
    .error {
      color: var(--status-overloaded);
      font-size: 12.5px;
      margin-top: 8px;
    }
  `;

  updated(changed) {
    if (changed.has('scenario') && this.scenario && !this._options.loaded) this._loadOptions();
  }

  /** The records the forms pick from: the base run's projects and deals, the stages, the sources. */
  async _loadOptions() {
    this._options = { ...this._options, loaded: true };
    const runId = this.appState.run?.id ?? null;
    const [projects, deals, stages, sources] = await Promise.all([
      runId
        ? api
            .get(`/forecast/runs/${runId}/projects`, { limit: 500, included: true })
            .catch(() => null)
        : null,
      runId
        ? api.get(`/forecast/runs/${runId}/opportunities`, { limit: 500 }).catch(() => null)
        : null,
      api.get('/scenarios/vocabulary').catch(() => null),
      api.get('/capacity/sources', { is_elastic: true, limit: 200 }).catch(() => null),
    ]);
    this._options = {
      loaded: true,
      projects: projects?.projects ?? [],
      deals: (deals?.opportunities ?? []).filter((d) => d.included),
      stages: (stages?.stages ?? []).filter((s) => s.open_deals > 0),
      sources: (sources?.sources ?? []).filter((s) => s.is_elastic && s.user),
    };
  }

  _emit() {
    this.dispatchEvent(new CustomEvent('scenario-changed', { bubbles: true, composed: true }));
  }

  async _patch(patch) {
    this._saving = true;
    this._error = null;
    try {
      await api.patch(`/scenarios/${this.scenario.id}`, patch);
      this._emit();
    } catch (err) {
      this._error = err instanceof ApiError ? err.message : String(err);
    } finally {
      this._saving = false;
    }
  }

  async _add() {
    const kind = this._adding;
    const body = { kind, ...this._draft };
    this._saving = true;
    this._error = null;
    try {
      if (kind === 'role_mix') body.role_mix = parseMix(this._draft.role_mix_text ?? '');
      delete body.role_mix_text;
      if (kind === 'elastic_source') {
        // An elastic source is the one override with a scenario-wide switch behind it, and a
        // planner who ticks a freelancer means "count it": the switch follows.
        await api.post(`/scenarios/${this.scenario.id}/overrides`, body);
        await api.post(`/scenarios/${this.scenario.id}/capacity-toggles`, {
          include_elastic: true,
          source_ids: [
            ...(this.scenario.overrides ?? [])
              .filter((o) => o.kind === 'elastic_source')
              .map((o) => o.capacity_source_id),
            body.capacity_source_id,
          ],
        });
      } else {
        await api.post(`/scenarios/${this.scenario.id}/overrides`, body);
      }
      this._adding = null;
      this._draft = {};
      this._emit();
    } catch (err) {
      this._error = err instanceof ApiError ? err.message : String(err);
    } finally {
      this._saving = false;
    }
  }

  async _remove(id) {
    this._saving = true;
    this._error = null;
    try {
      await api.delete(`/scenarios/${this.scenario.id}/overrides/${id}`);
      this._emit();
    } catch (err) {
      this._error = err instanceof ApiError ? err.message : String(err);
    } finally {
      this._saving = false;
    }
  }

  _set(key, value) {
    this._draft = { ...this._draft, [key]: value };
  }

  _form() {
    const kind = this._adding;
    if (!kind) return nothing;
    const meta = overrideMeta(kind);
    const { projects, deals, stages, sources } = this._options;
    const draft = this._draft;
    const ready =
      (kind === 'delivery_shift' && draft.project_id && Number(draft.shift_weeks)) ||
      (kind === 'role_mix' &&
        draft.project_id &&
        Object.keys(parseMix(draft.role_mix_text ?? '')).length) ||
      (kind === 'stage_exclude' && draft.stage_key) ||
      (kind === 'opportunity_exclude' && draft.opportunity_id) ||
      (kind === 'capacity' && draft.user_id && draft.weekly_hours !== undefined) ||
      (kind === 'elastic_source' && draft.capacity_source_id);

    const projectPicker = html`
      <select
        aria-label="Project"
        @change=${(e) => this._set('project_id', e.target.value || undefined)}
      >
        <option value="">Choose a project…</option>
        ${projects.map(
          (p) =>
            html`<option value=${p.project_id}>
              ${[p.source_project_number, p.name].filter(Boolean).join(' ')}
            </option>`,
        )}
      </select>
    `;

    return html`
      <div class="form">
        <span class="sentence">${meta.sentence}</span>
        ${
          kind === 'delivery_shift'
            ? html`
                ${projectPicker}
                <label class="inline">
                  weeks
                  <input
                    type="number"
                    min="-52"
                    max="52"
                    step="1"
                    style="width: 5.5em"
                    @change=${(e) => this._set('shift_weeks', Number(e.target.value))}
                  />
                </label>
              `
            : nothing
        }
        ${
          kind === 'role_mix'
            ? html`
                ${projectPicker}
                <input
                  type="text"
                  style="min-width: 22em"
                  placeholder="design 60, development 40"
                  aria-label="Role mix"
                  @change=${(e) => this._set('role_mix_text', e.target.value)}
                />
              `
            : nothing
        }
        ${
          kind === 'stage_exclude'
            ? html`
                <select aria-label="Stage" @change=${(e) => this._set('stage_key', e.target.value)}>
                  <option value="">Choose a stage…</option>
                  ${stages.map(
                    (s) =>
                      html`<option value=${s.key}>
                        ${s.stage_name} — ${s.open_deals}
                        open${s.planning_category ? ` · ${s.planning_category}` : ''}
                      </option>`,
                  )}
                </select>
              `
            : nothing
        }
        ${
          kind === 'opportunity_exclude'
            ? html`
                <select
                  aria-label="Deal"
                  @change=${(e) => this._set('opportunity_id', e.target.value)}
                >
                  <option value="">Choose a deal…</option>
                  ${deals.map(
                    (d) =>
                      html`<option value=${d.opportunity_id}>
                        ${d.name}${d.stage_name ? ` — ${d.stage_name}` : ''}
                      </option>`,
                  )}
                </select>
              `
            : nothing
        }
        ${
          kind === 'capacity'
            ? html`
                <select aria-label="Person" @change=${(e) => this._set('user_id', e.target.value)}>
                  <option value="">Choose a person…</option>
                  ${(this.appState.vocabularies?.people ?? []).map(
                    (p) => html`<option value=${p.id}>${p.display_name}</option>`,
                  )}
                </select>
                <label class="inline">
                  hours a week
                  <input
                    type="number"
                    min="0"
                    max="168"
                    step="0.25"
                    style="width: 6em"
                    @change=${(e) => this._set('weekly_hours', Number(e.target.value))}
                  />
                </label>
                <label class="inline">
                  from
                  <input type="date" @change=${(e) => this._set('active_from', e.target.value)} />
                </label>
                <label class="inline">
                  to
                  <input type="date" @change=${(e) => this._set('active_to', e.target.value)} />
                </label>
              `
            : nothing
        }
        ${
          kind === 'elastic_source'
            ? html`
                <select
                  aria-label="Elastic source"
                  @change=${(e) => this._set('capacity_source_id', e.target.value)}
                >
                  <option value="">Choose a source…</option>
                  ${sources.map(
                    (s) =>
                      html`<option value=${s.id}>
                        ${s.name ?? s.user?.display_name} — ${s.weekly_hours} h a week
                      </option>`,
                  )}
                </select>
                ${
                  sources.length
                    ? nothing
                    : html`<span class="sentence">
                        No elastic source is attached to a person yet. The forecast holds capacity
                        per person, so an unnamed pool cannot be put on the line: add a freelancer
                        or contractor against somebody under Settings → Capacity first.
                      </span>`
                }
              `
            : nothing
        }
        <button class="primary" ?disabled=${!ready || this._saving} @click=${() => this._add()}>
          Add
        </button>
        <button
          class="link"
          @click=${() => {
            this._adding = null;
            this._draft = {};
          }}
        >
          Cancel
        </button>
      </div>
    `;
  }

  render() {
    const s = this.scenario;
    if (!s) return nothing;
    const overrides = s.overrides ?? [];
    return html`
      <div class="grid">
        <section>
          <h3>What this scenario weighs</h3>
          <p class="hint">
            Left alone, a scenario follows the forecast's own settings, so recomputing it after a
            setting changes keeps it comparable with the base rather than frozen against it.
          </p>
          <div class="toggles">
            <label class="inline">
              Pipeline
              <select
                ?disabled=${this._saving}
                @change=${(e) => this._patch({ pipeline_mode: e.target.value || null })}
              >
                <option value="" ?selected=${!s.pipeline_mode}>as the forecast does</option>
                <option value="weighted" ?selected=${s.pipeline_mode === 'weighted'}>
                  weighted
                </option>
                <option value="unweighted" ?selected=${s.pipeline_mode === 'unweighted'}>
                  unweighted
                </option>
              </select>
            </label>
            ${PLANNING_CATEGORIES.map(
              (c) => html`
                <label class="inline">
                  <input
                    type="checkbox"
                    .checked=${(s.include_categories ?? PLANNING_CATEGORIES).includes(c)}
                    ?disabled=${this._saving}
                    @change=${(e) => {
                      const current = new Set(s.include_categories ?? PLANNING_CATEGORIES);
                      if (e.target.checked) current.add(c);
                      else current.delete(c);
                      this._patch({ include_categories: [...current] });
                    }}
                  />
                  ${c} stages
                </label>
              `,
            )}
          </div>
        </section>

        <section>
          <h3>What it changes</h3>
          ${
            overrides.length
              ? html`
                  <table>
                    <thead>
                      <tr>
                        <th scope="col">Change</th>
                        <th scope="col">What</th>
                        <th scope="col">To</th>
                        <th scope="col"><span class="sr-only">Remove</span></th>
                      </tr>
                    </thead>
                    <tbody>
                      ${overrides.map((o) => {
                        const d = describeOverride(o);
                        return html`
                          <tr>
                            <td>${d.label}</td>
                            <td class="subject">${d.subject}</td>
                            <td>${d.effect}</td>
                            <td>
                              <button
                                class="link"
                                ?disabled=${this._saving}
                                @click=${() => this._remove(o.id)}
                              >
                                Remove
                              </button>
                            </td>
                          </tr>
                        `;
                      })}
                    </tbody>
                  </table>
                `
              : html`<p class="empty">Nothing yet: this scenario computes to the base forecast.</p>`
          }
          <div class="add">
            <span class="kinds">
              ${OVERRIDE_META.map(
                (m) => html`
                  <button
                    ?disabled=${this._saving}
                    @click=${() => {
                      this._adding = m.kind;
                      this._draft = {};
                    }}
                  >
                    + ${m.label}
                  </button>
                `,
              )}
            </span>
          </div>
          ${this._form()} ${this._error ? html`<p class="error">${this._error}</p>` : nothing}
        </section>
      </div>
    `;
  }
}

/** `design 60, development 40` (or `design 0.6 development 0.4`) as shares; the API normalises. */
export function parseMix(text) {
  const out = {};
  for (const part of String(text ?? '').split(/[,;]+/)) {
    const match = /^\s*([A-Za-z0-9_\- ]+?)\s*[: ]\s*([0-9]*\.?[0-9]+)\s*%?\s*$/.exec(part);
    if (!match) continue;
    const service = match[1].trim().toLowerCase().replace(/\s+/g, '_');
    const value = Number(match[2]);
    if (service && value > 0) out[service] = (out[service] ?? 0) + value;
  }
  return out;
}

customElements.define('scenario-editor', ScenarioEditor);
export { ScenarioEditor };
